import React from 'react';
import { View, StyleSheet } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';

/**
 * One half of the Chat vs Work comparison. Header + short chips, then a
 * visual demo as `children` so the two cards stay balanced.
 */
export default function ModeCard({ mode, compact = false, children, style }) {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const tint = mode.color;

  return (
    <View
      style={[
        styles.card,
        {
          backgroundColor: isDark ? `${tint}10` : `${tint}07`,
          borderColor: isDark ? `${tint}38` : `${tint}30`,
        },
        style,
      ]}
    >
      <View style={styles.header}>
        <View style={[styles.iconWrap, { backgroundColor: tint }]}>
          <Icon name={mode.icon} size={compact ? 14 : 16} color="#fff" solid />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text weight="bold" style={{ fontSize: compact ? 17 : 19, letterSpacing: -0.3 }}>{mode.title}</Text>
          <Text size="xxs" weight="semibold" style={{ color: tint }}>{mode.tagline}</Text>
        </View>
        <View style={[styles.badge, { backgroundColor: isDark ? `${tint}26` : `${tint}18` }]}>
          <Text size="xxs" weight="bold" style={{ color: tint, fontSize: 9, letterSpacing: 0.4 }}>
            {mode.badge.toUpperCase()}
          </Text>
        </View>
      </View>

      <View style={styles.chips}>
        {mode.chips.map((chip) => (
          <View
            key={chip.label}
            style={[styles.chip, {
              backgroundColor: isDark ? `${tint}1C` : `${tint}14`,
              borderColor: isDark ? `${tint}30` : `${tint}24`,
            }]}
          >
            <Icon name={chip.icon} size={10} color={tint} solid />
            <Text size="xxs" weight="medium">{chip.label}</Text>
          </View>
        ))}
      </View>

      {children ? <View style={styles.demo}>{children}</View> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    borderRadius: 16,
    borderWidth: 1,
    padding: 14,
    gap: 10,
  },
  header: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  iconWrap: {
    width: 34,
    height: 34,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    paddingHorizontal: 7,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  demo: { flex: 1, minHeight: 168 },
});
